import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Label } from '@/components/ui/label';
import type { Mudanca } from '@/types/mudanca';
import { 
  Calendar, 
  Clock, 
  User, 
  Shield, 
  Target, 
  AlertTriangle,
  CheckCircle2,
  FileText
} from 'lucide-react';

interface MudancaDetalhesProps {
  mudanca: Mudanca;
}

const MudancaDetalhes = ({ mudanca }: MudancaDetalhesProps) => {
  const formatDateTime = (date?: string) => {
    return date ? new Date(date).toLocaleString('pt-BR') : 'N/A';
  };

  const getRiscoBadgeVariant = (risco?: string) => {
    switch (risco) {
      case 'baixo': return 'default';
      case 'medio': return 'secondary';
      case 'alto': return 'destructive';
      default: return 'outline';
    }
  };

  const getStatusBadgeVariant = (status?: string) => {
    switch (status) {
      case 'aprovada': return 'default';
      case 'sucesso': return 'default';
      case 'em_execucao': return 'secondary';
      case 'cancelada': return 'destructive';
      case 'rollback_executado': return 'destructive';
      default: return 'outline';
    }
  };

  return (
    <div className="space-y-6">
      {/* Informações Gerais */}
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <FileText className="w-5 h-5" />
            Informações Gerais
          </CardTitle>
        </CardHeader>
        <CardContent className="space-y-4">
          <div className="grid grid-cols-2 gap-6">
            <div>
              <Label className="text-sm font-semibold flex items-center gap-2">
                <CheckCircle2 className="w-4 h-4" />
                Número da Mudança
              </Label>
              <p className="text-sm mt-1">{mudanca.numero}</p>
            </div>

            <div>
              <Label className="text-sm font-semibold flex items-center gap-2">
                <Shield className="w-4 h-4" />
                Status
              </Label>
              <Badge variant={getStatusBadgeVariant(mudanca.status)} className="mt-1">
                {mudanca.status}
              </Badge>
            </div>

            <div>
              <Label className="text-sm font-semibold flex items-center gap-2">
                <Target className="w-4 h-4" />
                Tipo de Mudança
              </Label>
              <p className="text-sm mt-1">{mudanca.tipo_mudanca || 'N/A'}</p>
            </div>

            <div>
              <Label className="text-sm font-semibold flex items-center gap-2">
                <AlertTriangle className="w-4 h-4" />
                Classificação de Risco
              </Label>
              <Badge variant={getRiscoBadgeVariant(mudanca.classificacao_risco)} className="mt-1">
                {mudanca.classificacao_risco || 'N/A'}
              </Badge>
            </div>
          </div>
        </CardContent>
      </Card>

      {/* Justificativas */}
      <Card>
        <CardHeader>
          <CardTitle>Justificativas</CardTitle>
        </CardHeader>
        <CardContent className="space-y-4">
          <div>
            <Label className="text-sm font-semibold">Justificativa Técnica</Label>
            <p className="text-sm mt-1 p-3 bg-muted rounded-md">
              {mudanca.justificativa_tecnica || 'Não informada'}
            </p>
          </div>

          <div>
            <Label className="text-sm font-semibold">Justificativa de Negócio</Label>
            <p className="text-sm mt-1 p-3 bg-muted rounded-md">
              {mudanca.justificativa_negocio || 'Não informada'}
            </p>
          </div>
        </CardContent>
      </Card>

      {/* Planejamento */}
      <Card>
        <CardHeader>
          <CardTitle>Planejamento</CardTitle>
        </CardHeader>
        <CardContent className="space-y-4">
          <div>
            <Label className="text-sm font-semibold">Plano de Implementação</Label>
            <p className="text-sm mt-1 p-3 bg-muted rounded-md whitespace-pre-wrap">
              {mudanca.plano_implementacao || 'Não informado'}
            </p>
          </div>

          <div>
            <Label className="text-sm font-semibold">Plano de Testes</Label>
            <p className="text-sm mt-1 p-3 bg-muted rounded-md whitespace-pre-wrap">
              {mudanca.plano_testes || 'Não informado'}
            </p>
          </div>

          <div>
            <Label className="text-sm font-semibold">Plano de Rollback</Label>
            <p className="text-sm mt-1 p-3 bg-muted rounded-md whitespace-pre-wrap">
              {mudanca.plano_rollback || 'Não informado'}
            </p>
          </div>
        </CardContent>
      </Card>

      {/* Análise de Impacto e Riscos */}
      <Card>
        <CardHeader>
          <CardTitle>Análise de Impacto e Riscos</CardTitle>
        </CardHeader>
        <CardContent className="space-y-4">
          <div>
            <Label className="text-sm font-semibold">Impacto Estimado</Label>
            <p className="text-sm mt-1 p-3 bg-muted rounded-md whitespace-pre-wrap">
              {mudanca.impacto_estimado || 'Não informado'}
            </p>
          </div>

          <div>
            <Label className="text-sm font-semibold">Riscos Identificados</Label>
            <p className="text-sm mt-1 p-3 bg-muted rounded-md whitespace-pre-wrap">
              {mudanca.riscos_identificados || 'Não informados'}
            </p>
          </div>
        </CardContent>
      </Card>

      {/* Cronograma e Datas */}
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <Calendar className="w-5 h-5" />
            Cronograma e Datas
          </CardTitle>
        </CardHeader>
        <CardContent className="space-y-4">
          <div className="grid grid-cols-2 gap-6">
            <div>
              <Label className="text-sm font-semibold flex items-center gap-2">
                <Clock className="w-4 h-4" />
                Data de Abertura
              </Label>
              <p className="text-sm mt-1">{formatDateTime(mudanca.data_abertura)}</p>
            </div>

            <div>
              <Label className="text-sm font-semibold flex items-center gap-2">
                <Calendar className="w-4 h-4" />
                Execução Planejada
              </Label>
              <p className="text-sm mt-1">{formatDateTime(mudanca.data_execucao_planejada)}</p>
            </div>

            {mudanca.janela_manutencao_inicio && (
              <>
                <div>
                  <Label className="text-sm font-semibold">Janela de Manutenção - Início</Label>
                  <p className="text-sm mt-1">{formatDateTime(mudanca.janela_manutencao_inicio)}</p>
                </div>

                <div>
                  <Label className="text-sm font-semibold">Janela de Manutenção - Fim</Label>
                  <p className="text-sm mt-1">{formatDateTime(mudanca.janela_manutencao_fim)}</p>
                </div>
              </>
            )}

            {mudanca.data_aprovacao && (
              <div>
                <Label className="text-sm font-semibold">Data de Aprovação</Label>
                <p className="text-sm mt-1">{formatDateTime(mudanca.data_aprovacao)}</p>
              </div>
            )}
          </div>
        </CardContent>
      </Card>

      {/* SLA */}
      {mudanca.sla_prazo_horas && (
        <Card>
          <CardHeader>
            <CardTitle className="flex items-center gap-2">
              <Clock className="w-5 h-5" />
              Informações de SLA
            </CardTitle>
          </CardHeader>
          <CardContent className="space-y-4">
            <div className="grid grid-cols-2 gap-6">
              <div>
                <Label className="text-sm font-semibold">Prazo SLA (horas)</Label>
                <p className="text-sm mt-1">{mudanca.sla_prazo_horas}h</p>
              </div>

              {mudanca.data_sla_limite && (
                <div>
                  <Label className="text-sm font-semibold">Data Limite SLA</Label>
                  <p className="text-sm mt-1">{formatDateTime(mudanca.data_sla_limite)}</p>
                </div>
              )}
            </div>
          </CardContent>
        </Card>
      )}

      {/* Auditoria */}
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <User className="w-5 h-5" />
            Auditoria
          </CardTitle>
        </CardHeader>
        <CardContent className="space-y-4">
          <div className="grid grid-cols-2 gap-6">
            <div>
              <Label className="text-sm font-semibold">Criado em</Label>
              <p className="text-sm mt-1">{formatDateTime(mudanca.criado_em)}</p>
            </div>

            <div>
              <Label className="text-sm font-semibold">Última Atualização</Label>
              <p className="text-sm mt-1">{formatDateTime(mudanca.atualizado_em)}</p>
            </div>
          </div>
        </CardContent>
      </Card>
    </div>
  );
};

export default MudancaDetalhes;
